/**
 * PlanDetailPortalPage — Versión INTERNA del detalle de plan.
 *
 * Vive bajo PortalLayout (con sidebar). Usa el mismo hook `usePlanDetail` que
 * la versión pública pero sin TopNavbar/footer/heroes públicos: cards limpias
 * en el estilo del portal.
 *
 * Ruta: /mi-cuenta/planes/:planId
 */

import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import { usePlanDetail } from '@modules/plans/presentation/hooks/usePlans';
import { getPlanPDFDownloadURL } from '@modules/assets/infrastructure/repositories/http/AssetsHttpRepository';
import { formatCLP, formatUF } from '@shared/utils/formatters';
import {
  ArrowLeft,
  FileText,
  Star,
  ShieldCheck,
  Search,
  CheckCircle2,
} from 'lucide-react';

const TIER_STARS: Record<string, number> = {
  bronze: 1,
  silver: 2,
  platinum: 2,
  gold: 3,
};

function starsForTier(tier: string | null | undefined): number {
  if (!tier) return 1;
  return TIER_STARS[tier.toLowerCase()] ?? 1;
}

function formatPct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function PlanDetailPortalPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePlanDetail(planId);

  useEffect(() => {
    if (!data) return;
    const prev = document.title;
    document.title = `Plan ${data.plan.name} — mishiwoof`;
    return () => {
      document.title = prev;
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-40 rounded-full" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Plan no encontrado" subtitle="No pudimos cargar este plan" />
        <EmptyState
          icon={Search}
          title="Plan no disponible"
          description="Este plan puede haber sido despublicado o el enlace está incorrecto."
          action={
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/sucursal-virtual">Volver al inicio</Link>
            </Button>
          }
        />
      </div>
    );
  }

  const { plan, coverages, annual_cap_uf, uf_value_today, annual_cap_note } = data;
  const priceCLP = uf_value_today > 0 ? plan.price_uf * uf_value_today : 0;
  const stars = starsForTier(plan.tier);

  return (
    <div className="space-y-6">
      {/* Back nav */}
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 rounded-full text-muted-foreground hover:text-foreground"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
        Volver
      </Button>

      {/* Hero card */}
      <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-xs">
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div className="space-y-3">
            <div className="flex items-center gap-1" aria-label={`Plan ${plan.tier ?? ''}`}>
              {Array.from({ length: stars }).map((_, i) => (
                <Star
                  key={i}
                  className="h-4 w-4 fill-chart-4 text-chart-4"
                  aria-hidden="true"
                />
              ))}
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Plan {plan.name}
            </h1>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-2xl font-bold text-primary">{formatUF(plan.price_uf)}</span>
              <span className="text-sm text-muted-foreground">/ mes</span>
              {priceCLP > 0 && (
                <span className="text-sm text-muted-foreground">
                  · ~{formatCLP(Math.round(priceCLP))}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {plan.pdf_url && planId && (
              <Button asChild variant="outline" className="rounded-full">
                <a
                  href={getPlanPDFDownloadURL(planId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FileText className="mr-1 h-4 w-4" aria-hidden="true" />
                  Ver PDF
                </a>
              </Button>
            )}
            <Button className="rounded-full" asChild>
              <Link to="/contratacion">Contratar este plan</Link>
            </Button>
          </div>
        </div>

        {plan.hero_image_url && (
          <div
            className="hidden h-32 w-full bg-cover bg-center sm:block"
            style={{ backgroundImage: `url("${plan.hero_image_url}")` }}
            aria-hidden="true"
          />
        )}
      </section>

      {/* Coverages */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-xs sm:p-8">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <ShieldCheck className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-lg font-bold tracking-tight text-foreground">
              Coberturas incluidas
            </h2>
            <p className="text-xs text-muted-foreground">
              Detalle de prestaciones cubiertas por este plan.
            </p>
          </div>
        </div>

        {coverages.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Este plan aún no tiene coberturas configuradas.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {coverages.map((cv) => {
              const has = cv.coverage_percentage > 0;
              return (
                <li
                  key={cv.id}
                  className="rounded-xl border border-border bg-muted/30 p-4"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                        has
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <CheckCircle2
                        className="h-4 w-4"
                        strokeWidth={1.75}
                        aria-hidden="true"
                      />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold uppercase tracking-wide text-foreground">
                        {cv.name}
                      </p>
                      {has ? (
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {formatPct(cv.coverage_percentage)}
                          {cv.cap_uf_per_event != null && (
                            <> · tope {formatUF(cv.cap_uf_per_event)}/evento</>
                          )}
                        </p>
                      ) : (
                        <p className="mt-0.5 text-sm italic text-muted-foreground">Sin cobertura</p>
                      )}
                      {cv.events_annual != null && cv.events_annual > 0 && (
                        <p className="mt-0.5 text-xs text-sidebar-primary">
                          Hasta {cv.events_annual} eventos al año
                        </p>
                      )}
                      {cv.disclaimer && (
                        <p className="mt-1 text-xs italic text-muted-foreground">{cv.disclaimer}</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Annual cap */}
      <section className="rounded-2xl border border-border bg-accent/40 p-6 shadow-xs sm:p-8">
        <p className="text-xs font-medium uppercase tracking-wide text-sidebar-primary">
          Tope anual
        </p>
        <p className="mt-1 text-3xl font-bold text-foreground">{formatUF(annual_cap_uf)}</p>
        <p className="mt-2 text-sm text-muted-foreground">{annual_cap_note}</p>
      </section>
    </div>
  );
}
